import React, { useEffect } from "react";
import { RouteComponentProps, useNavigate, useParams } from "@reach/router";
import { HeadingLarge } from "kanopi-components/typography";
import { Block } from "kanopi-components/Block";
import { PortalRow } from "kanopi-components/PortalRow";
import { PolicyFixedFooter } from "../components/PolicyFixedFooter";
import { NavButton } from "kw/components/NavButton/NavButton";
import { usePolicy } from "../hooks/usePolicy";
import { Placeholder } from "kanopi-components/Placeholder";
import { usePolicyCancellationQuote } from "../hooks/useCancelPolicy";
import { Callout } from "kanopi-components/Callout";
import { TTableRow, TwoColumnTable } from "kw/components/TabularDisplay/TwoColumnTable";
import { useAPI } from "kw/api/useAPI";

type TPolicyCancelPage = {
  policyId: string;
};
export const PolicyCancellationPage: React.FC<RouteComponentProps> = () => {
  const params = useParams<TPolicyCancelPage>();
  const navigate = useNavigate();

  const [policy, { policyLoading }] = usePolicy(params?.policyId);
  const policyDetails: TTableRow[] = [
    {
      label: "Policy number",
      value: policy?.policyNumber,
    },
    {
      label: "Policy start date",
      value: policy?.startDate,
    },
    {
      label: "Policy finish date",
      value: policy?.finishDate,
    },
  ];
  const riskDataString = policy?.quote?.riskProfile?.riskData;

  let riskData;

  if (riskDataString) {
    try {
      riskData = JSON.parse(riskDataString);
    } catch (error) {
      console.error("Failed to parse riskData JSON string:", error);
    }
  }

  const cancellationDetails = riskData ? riskData["cancellation"] : undefined;

  if (cancellationDetails) {
    Object.entries(cancellationDetails).forEach(([key, value]) => {
      policyDetails.push({
        label: `Cancellation ${key
          .replace(/([A-Z])/g, " $1")
          .replace(/_/g, " ")
          .toLowerCase()}`,
        value: value,
      });
    });
  }
  const { loading: policyDetailsLoading } = useAPI();

  const {
    createPolicyCancellationQuote,
    isSuccess: cancellationQuoteSubmissionSuccess,
    loading: policyCancellationQuoteLoading,
  } = usePolicyCancellationQuote(params?.policyId);

  const handleConfirmCancellation = async () => {
    await createPolicyCancellationQuote();
  };
  useEffect(() => {
    if (policy?.isPendingCancellation) {
      navigate(`/policies/${params?.policyId}/`);
    }
  }, [policy]);

  useEffect(() => {
    if (cancellationQuoteSubmissionSuccess) {
      navigate(`/policies/${params?.policyId}/cancel/confirm`);
    }
  }, [cancellationQuoteSubmissionSuccess]);

  return (
    <Block
      display="flex"
      justifyItems="center"
      flexDirection="column"
      alignItems="center"
      width="100%"
    >
      <Placeholder loading={policyLoading}>
        <PortalRow>
          <HeadingLarge margin={0} center>
            Cancel your policy
          </HeadingLarge>
          <PortalRow>
            <Callout>
              Please review your cancellation details before submitting it.
            </Callout>
          </PortalRow>
        </PortalRow>
        <Placeholder loading={policyDetailsLoading}>
          <PortalRow>
            <TwoColumnTable id="claimState-PolicyDetails" rows={policyDetails} />
          </PortalRow>
        </Placeholder>
        <PolicyFixedFooter>
          <NavButton
            onClick={handleConfirmCancellation}
            isLoading={policyCancellationQuoteLoading}
          >
            Confirm cancellation
          </NavButton>
        </PolicyFixedFooter>
      </Placeholder>
    </Block>
  );
};
